import { Space, Empty } from 'antd'
import type { RefSelectProps } from 'antd/es/select'
import { LabeledValue, SelectProps as AntSelectProps } from 'antd/lib/select'
import classNames from 'classnames'
import _ from 'lodash'
import React, { useCallback, useRef, useState } from 'react'
import { SpaceProps } from 'styled-system'

import { getGlobalUniqueNumber } from '../../utils/helpers'
import { useWindowScroll } from '../../hooks/useWindowScroll'
import { CenterChildren } from '../Center'
import { Spin, SpinSize } from '../Spin'
import { ThemeWrapper } from '../theme'
import { Input } from '../Input'
import { StyledMulticolumnSelect } from '../Select/helper'
import { SelectOption, SelectVariants } from '../Select'

import MulticolumnList from './MulticolumnList'

export interface MultiColumnSelectProps
  extends React.PropsWithChildren<
    AntSelectProps<React.ReactText | React.ReactText[] | LabeledValue | LabeledValue[] | undefined> & SpaceProps
  > {
  $colorVariant?: SelectVariants
  options?: SelectOption[]
  $orientation?: 'vertical' | 'horizontal'
  showOptionsOnLoading?: boolean
  inputCustomization?: boolean
  popupContainerClassName?: string
  numberOfColumns?: number
  top?: string
  width?: string
  setCssOptions?: (element: Element | null) => void
  isMobile?: boolean
  SearchMode?: 'MetaRow' | 'Inline' | 'None'
}

const defaultProps: MultiColumnSelectProps = {
  $colorVariant: 'default'
}

function findParentByClassName(className: string): HTMLElement | null {
  const parent = document.querySelector(className)
  if (parent) {
    return parent.parentElement
  }
  return null
}

export const MulticolumnDropdown = ({
  options,
  value,
  showOptionsOnLoading = false,
  inputCustomization = true,
  onChange,
  popupContainerClassName,
  numberOfColumns,
  setCssOptions,
  top,
  width,
  isMobile,
  SearchMode = 'None',
  ...rest
}: MultiColumnSelectProps) => {
  const selectRef = useRef<RefSelectProps>(null)
  const [uniqueClassName] = useState<string>(`unique-select-${getGlobalUniqueNumber()}`)
  const [filter, setFilter] = useState('')
  const columns = numberOfColumns ? numberOfColumns : 1
  const selectedOption = options?.filter(p => p.value === value)

  const setSelectedOption = (country: string) => {
    const selectedOption = options?.filter(p => p.value === country)
    selectedOption && onChange && onChange(country, selectedOption)
    setFilter('')
    if (isMobile) {
      const select = document.querySelector('.ant-select-selector')
      const clickEvent = document.createEvent('MouseEvents')
      clickEvent.initEvent('mousedown', true, true)
      select && select.dispatchEvent(clickEvent)
    } else {
      selectRef.current?.blur()
    }
  }

  const onSearch = (value: string) => {
    setFilter(value)
  }

  const filteredItems = _.filter(
    options,
    o => _.includes(_.toString(o.label).toLowerCase(), filter.toLowerCase()) || filter === ''
  )

  useWindowScroll(false, () => {
    setFilter('')
    selectRef.current?.blur()
  })

  const setTopByRef = useCallback(() => {
    setCssOptions && setCssOptions(document.getElementsByClassName(uniqueClassName)?.item(0))
  }, [uniqueClassName, setCssOptions])

  return (
    <ThemeWrapper>
      <StyledMulticolumnSelect
        className={classNames({ ['ant-select-customize-input']: inputCustomization }, uniqueClassName)}
        {...defaultProps}
        {..._.omit(rest, 'error')}
        value={_.isEmpty(value) ? undefined : value}
        ref={selectRef}
        onDropdownVisibleChange={setTopByRef}
        getPopupContainer={() =>
          popupContainerClassName ? findParentByClassName(popupContainerClassName) ?? document.body : document.body
        }
        onSearch={onSearch}
        showSearch={SearchMode === 'Inline'}
        dropdownRender={
          rest.loading && (!showOptionsOnLoading || !options || options.length === 0)
            ? () => (
                <CenterChildren my={2}>
                  <Spin size={SpinSize.Large} />
                </CenterChildren>
              )
            : () => (
                <>
                  {options && options.length > 0 ? (
                    <div
                      className={`mutlicolumn-dropdown-select${isMobile ? '-mobile' : ''}`}
                      style={{ width: width, top: top }}
                    >
                      {SearchMode === 'MetaRow' && (
                        <Input
                          value={filter}
                          onChange={event => onSearch(event.target.value)}
                          className="mutlicolumn-dropdown-select-input"
                          placeholder={
                            selectedOption && selectedOption?.length > 0 ? _.toString(selectedOption[0].label) : ''
                          }
                        />
                      )}
                      {_.size(filteredItems) > 0 ? (
                        <Space className="mutlicolumn-dropdown-space">
                          <div className="itemsContainer">
                            <MulticolumnList
                              items={filteredItems}
                              onChange={setSelectedOption}
                              numberOfColumns={columns}
                              selectedOption={selectedOption && selectedOption[0]}
                            />
                          </div>
                        </Space>
                      ) : (
                        <CenterChildren pt={1}>
                          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        </CenterChildren>
                      )}
                    </div>
                  ) : undefined}
                </>
              )
        }
        options={options}
      />
    </ThemeWrapper>
  )
}
